import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCamera, FaStop, FaUser, FaBuilding, FaCheckCircle, FaTimes, FaSpinner, FaQrcode } from 'react-icons/fa';
import { linkageService } from '../services/linkageService';

interface ScannedEmployee {
  userId: string;
  userData: any;
  scannedCode: string;
  scannedAt: Date;
}

const DynamicCodeTest: React.FC = () => {
  const { t } = useTranslation();
  const [isScanning, setIsScanning] = useState(false);
  const [scannedEmployee, setScannedEmployee] = useState<ScannedEmployee | null>(null);
  const [scanError, setScanError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Función para activar la cámara
  const startCamera = async () => {
    try {
      setIsLoading(true);
      setScanError('');
      setScannedEmployee(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Usar cámara trasera si está disponible
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsScanning(true);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setScanError('No se pudo acceder a la cámara. Verifica los permisos.');
    } finally {
      setIsLoading(false);
    }
  };

  // Función para detener la cámara
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  };

  // Función para capturar frame y procesar
  const captureAndProcess = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Configurar canvas
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Capturar frame
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Aquí normalmente usarías una librería de OCR o reconocimiento de códigos
    // Por ahora, simularemos el proceso
    await simulateCodeRecognition();
  };

  // Simulación de reconocimiento de código
  const simulateCodeRecognition = async () => {
    setIsLoading(true);
    
    // Simular tiempo de procesamiento
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      // Generar un código de prueba
      const testCode = 'TEST1234';
      
      // Buscar empleado por código
      const employee = await linkageService.findEmployeeByCode(testCode);
      
      if (employee) {
        setScannedEmployee({
          userId: employee.userId,
          userData: employee.userData,
          scannedCode: testCode,
          scannedAt: new Date()
        });
        stopCamera();
      } else {
        setScanError('Código no reconocido. Intenta de nuevo.');
      }
    } catch (error) {
      setScanError('Error al procesar el código.');
    } finally {
      setIsLoading(false);
    }
  };

  // Función para entrada manual de código
  const handleManualCodeSubmit = async () => {
    if (!manualCode.trim()) return;

    setIsLoading(true);
    setScanError('');
    setScannedEmployee(null);

    try {
      const employee = await linkageService.findEmployeeByCode(manualCode.trim());
      
      if (employee) {
        setScannedEmployee({
          userId: employee.userId,
          userData: employee.userData,
          scannedCode: manualCode.trim(),
          scannedAt: new Date()
        });
      } else {
        setScanError('Código no encontrado o expirado.');
      }
    } catch (error) {
      setScanError('Error al procesar el código.');
    } finally {
      setIsLoading(false);
    }
  };

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t('dynamicCodeTest.title', 'Dynamic Code Scanner Test')}
          </h1>
          <p className="text-gray-600">
            {t('dynamicCodeTest.subtitle', 'Test the dynamic code scanning functionality')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Panel de Escaneo */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FaQrcode className="text-blue-500" />
              {t('dynamicCodeTest.scanner', 'Code Scanner')}
            </h2>

            {/* Controles de cámara */}
            <div className="mb-6">
              {!isScanning ? (
                <button
                  onClick={startCamera}
                  disabled={isLoading}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <FaSpinner className="animate-spin" />
                  ) : (
                    <FaCamera />
                  )}
                  {isLoading ? t('dynamicCodeTest.starting', 'Starting Camera...') : t('dynamicCodeTest.startCamera', 'Start Camera')}
                </button>
              ) : (
                <button
                  onClick={stopCamera}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FaStop />
                  {t('dynamicCodeTest.stopCamera', 'Stop Camera')}
                </button>
              )}
            </div>

            {/* Área de cámara */}
            <div className="relative mb-6">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-300">
                {isScanning ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <FaCamera className="text-4xl" />
                  </div>
                )}
              </div>
              
              {/* Canvas oculto para procesamiento */}
              <canvas ref={canvasRef} className="hidden" />
              
              {/* Overlay de escaneo */}
              {isScanning && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-500/10">
                    <div className="text-blue-500 font-semibold text-center">
                      {t('dynamicCodeTest.scanning', 'Scanning...')}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Botón de captura */}
            {isScanning && (
              <button
                onClick={captureAndProcess}
                disabled={isLoading}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  <FaCheckCircle />
                )}
                {isLoading ? t('dynamicCodeTest.processing', 'Processing...') : t('dynamicCodeTest.capture', 'Capture & Process')}
              </button>
            )}

            {/* Entrada manual */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-3">
                {t('dynamicCodeTest.manualEntry', 'Manual Code Entry')}
              </h3>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder={t('dynamicCodeTest.enterCode', 'Enter code manually...')}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={handleManualCodeSubmit}
                  disabled={!manualCode.trim() || isLoading}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('dynamicCodeTest.submit', 'Submit')}
                </button>
              </div>
            </div>

            {/* Mensajes de error */}
            {scanError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-600">
                  <FaTimes />
                  <span>{scanError}</span>
                </div>
              </div>
            )}
          </div>

          {/* Panel de Resultados */}
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <FaUser className="text-green-500" />
              {t('dynamicCodeTest.results', 'Scan Results')}
            </h2>

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <FaSpinner className="text-4xl text-blue-500 animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">
                    {t('dynamicCodeTest.processingCode', 'Processing code...')}
                  </p>
                </div>
              </div>
            ) : scannedEmployee ? (
              <div className="space-y-4">
                {/* Información del empleado */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-600 mb-2">
                    <FaCheckCircle />
                    <span className="font-semibold">
                      {t('dynamicCodeTest.employeeFound', 'Employee Found!')}
                    </span>
                  </div>
                  <p className="text-sm text-green-700">
                    {t('dynamicCodeTest.scannedAt', 'Scanned at:')} {scannedEmployee.scannedAt.toLocaleString()}
                  </p>
                </div>

                {/* Datos del empleado */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                    <FaUser />
                    {t('dynamicCodeTest.employeeInfo', 'Employee Information')}
                  </h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('dynamicCodeTest.name', 'Name:')}</span>
                      <span className="font-medium">
                        {scannedEmployee.userData.firstName} {scannedEmployee.userData.lastName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('dynamicCodeTest.email', 'Email:')}</span>
                      <span className="font-medium">{scannedEmployee.userData.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('dynamicCodeTest.userId', 'User ID:')}</span>
                      <span className="font-medium font-mono text-xs">{scannedEmployee.userId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('dynamicCodeTest.scannedCode', 'Scanned Code:')}</span>
                      <span className="font-medium font-mono">{scannedEmployee.scannedCode}</span>
                    </div>
                  </div>
                </div>

                {/* Botón para asignar */}
                <button
                  onClick={() => {
                    // Aquí iría la lógica para asignar el empleado
                    alert('Employee assignment functionality would go here');
                  }}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <FaBuilding />
                  {t('dynamicCodeTest.assignEmployee', 'Assign Employee')}
                </button>

                {/* Botón para limpiar */}
                <button
                  onClick={() => {
                    setScannedEmployee(null);
                    setScanError('');
                    setManualCode('');
                  }}
                  className="w-full bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                >
                  {t('dynamicCodeTest.clearResults', 'Clear Results')}
                </button>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FaQrcode className="text-4xl mx-auto mb-4" />
                <p>{t('dynamicCodeTest.noResults', 'No scan results yet.')}</p>
                <p className="text-sm mt-2">
                  {t('dynamicCodeTest.scanInstructions', 'Start the camera and scan a dynamic code to see results.')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicCodeTest; 